"use client";

import { FC, useEffect, useRef, useState } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { motion } from "framer-motion";
import Image from "next/image";
import noise from "@/assets/noise.png";
import studentBenefitsCardShape from "@/assets/studentBenefitsCardShape.svg";
import clsx from "clsx";
import { PrismicNextImage } from "@prismicio/next";
import CircleType from "circletype";

/**
 * Props for `StudentBenefitsSection`.
 */
export type StudentBenefitsSectionProps =
  SliceComponentProps<Content.StudentBenefitsSectionSlice>;

/**
 * Component for "StudentBenefitsSection" Slices.
 */
const StudentBenefitsSection: FC<StudentBenefitsSectionProps> = ({ slice }) => {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const circleTextRef = useRef<HTMLDivElement | null>(null);
  const circleTextRefMobile = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    new CircleType(circleTextRef.current);
    new CircleType(circleTextRefMobile.current);
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="py-20 bg-white"
    >
      <div className="relative">
        <Bounded className="container mx-auto">
          <div className="flex flex-col xl:flex-row items-center justify-between gap-[104px]">
            <div className="xl:max-w-[632px] max-w-full flex flex-col xl:items-start items-center justify-center">
              <motion.div
                className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-6 text-center xl:text-left"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1 }}
              >
                <PrismicRichText
                  field={slice.primary.title}
                  components={{
                    label: ({ node, children }) => {
                      if (node.data.label === "Highlight") {
                        return (
                          <span className="text-secondary">{children}</span>
                        );
                      }
                      return <span className="text-primary">{children}</span>;
                    },
                  }}
                />
              </motion.div>
              <motion.div
                className="font-merriweather w-full font-normal text-xl leading-8 xl:text-left text-center text-[#033D61] mb-8 max-w-[569px]"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.3 }}
              >
                <PrismicRichText field={slice.primary.description} />
              </motion.div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {slice.primary.benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.title}
                    className="h-full"
                    onHoverStart={() => {
                      setHoveredCard(index);
                    }}
                    onHoverEnd={() => {
                      setHoveredCard(null);
                    }}
                  >
                    <motion.div
                      className="relative h-full transform-3d perspective-[1000px]"
                      animate={{
                        rotateY: hoveredCard === index ? 180 : 0,
                      }}
                      transition={{
                        duration: 0.5,
                        delay: 0.3,
                        ease: "easeOut",
                      }}
                    >
                      <div className="backface-hidden px-[33px] py-9 text-[#033D61] rounded-xl overflow-hidden h-full bg-[#FAFDFF]">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center mb-8 bg-[#EBF7FF]">
                          <PrismicNextImage
                            field={benefit.icon}
                            width={24}
                            height={24}
                          />
                        </div>
                        <div className="font-archivo text-xl font-semibold leading-8 mb-3.5">
                          {benefit.title}
                        </div>
                        <div className="font-merriweather text-sm font-normal leading-6">
                          <PrismicRichText
                            field={benefit.description}
                            components={{
                              list: ({ node, children }) => {
                                return (
                                  <div className="list-none list-inside mt-3.5">
                                    {children}
                                  </div>
                                );
                              },
                              listItem: ({ node, children }) => {
                                return <li>⦿ {children}</li>;
                              },
                              oList: ({ node, children }) => {
                                return (
                                  <div className="list-decimal list-inside mt-3.5">
                                    {children}
                                  </div>
                                );
                              },
                              label: ({ node, children }) => {
                                if (node.data.label === "Highlight") {
                                  return (
                                    <span className="font-bold">
                                      {children}
                                    </span>
                                  );
                                }
                                return (
                                  <span className="font-normal">
                                    {children}
                                  </span>
                                );
                              },
                            }}
                          />
                        </div>
                      </div>
                      <div className="absolute inset-0 backface-hidden rotate-y-180 rounded-xl overflow-hidden">
                        <div className="relative w-full h-full px-[33px] py-9 bg-[#045B91] text-[#FAFDFF]">
                          <div
                            className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.3]"
                            style={{
                              background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
                            }}
                          />
                          <Image
                            src={studentBenefitsCardShape.src}
                            alt="shape"
                            width={473}
                            height={439}
                            className="absolute h-[120%] w-full top-[-60px] -left-0.5 pointer-events-none select-none"
                          />
                          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-8 bg-[#D9F7F21A]">
                            <PrismicNextImage
                              field={benefit.reverted_icon}
                              width={24}
                              height={24}
                            />
                          </div>
                          <div className="font-archivo relative text-xl font-semibold leading-8 mb-3.5">
                            {benefit.title}
                          </div>
                          <div className="font-merriweather text-sm font-normal leading-6">
                            <PrismicRichText
                              field={benefit.description}
                              components={{
                                list: ({ node, children }) => {
                                  return (
                                    <div className="list-none list-inside mt-3.5">
                                      {children}
                                    </div>
                                  );
                                },
                                listItem: ({ node, children }) => {
                                  return <li>⦿ {children}</li>;
                                },
                                oList: ({ node, children }) => {
                                  return (
                                    <div className="list-decimal list-inside mt-3.5">
                                      {children}
                                    </div>
                                  );
                                },
                                label: ({ node, children }) => {
                                  if (node.data.label === "Highlight") {
                                    return (
                                      <span className="font-bold">
                                        {children}
                                      </span>
                                    );
                                  }
                                  return (
                                    <span className="font-normal">
                                      {children}
                                    </span>
                                  );
                                },
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="block xl:hidden w-full">
              <div className="relative">
                <PrismicNextImage
                  field={slice.primary.right_image}
                  className="h-full w-full rounded-3xl object-cover"
                />
                <div className="absolute top-[-59px] right-[69px] md:right-[131px] lg:right-[-3px] h-[137px] w-[137px] md:h-[200px] md:w-[200px]">
                  <div className="relative h-full w-full bg-[#0798F2] rounded-full flex items-center justify-center">
                    <motion.div
                      ref={circleTextRefMobile}
                      className="absolute text-[#EBF7FF] text-[10px] md:text-[15px]"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      {slice.primary.circle_text}
                    </motion.div>
                    <div className="md:h-[130px] md:w-[130px] h-[90px] w-[90px] rounded-full bg-[#011E30] flex items-center justify-center">
                      <PrismicNextImage field={slice.primary.circle_icon} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute right-0 h-full xl:block hidden">
              <div className="relative">
                <PrismicNextImage
                  field={slice.primary.right_image}
                  className="h-full rounded-l-3xl object-cover"
                />
                <div className="absolute bottom-[-100px] left-[-100px] right-0 h-[200px] w-[200px]">
                  <div className="relative h-full w-full bg-[#0798F2] rounded-full flex items-center justify-center">
                    <motion.div
                      ref={circleTextRef}
                      className="absolute text-[#EBF7FF] text-[15px]"
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      {slice.primary.circle_text}
                    </motion.div>
                    <div className="md:h-[130px] md:w-[130px] h-[90px] w-[90px] rounded-full bg-[#011E30] flex items-center justify-center">
                      <PrismicNextImage field={slice.primary.circle_icon} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Bounded>
      </div>
    </section>
  );
};

export default StudentBenefitsSection;
